// src/pages/events/AttendancePointsDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TextField, Grid, CircularProgress,
  Alert, Divider, Tooltip, Chip, Stack
} from "@mui/material";
import { ref, onValue, off, get, update } from "firebase/database";
import { db } from "../../firebase";

export default function AttendancePointsDialog({ open, onClose, event }) {
  const [employees, setEmployees] = useState({});
  const [points, setPoints] = useState({});             // attendance (0–4)
  const [perfIndex, setPerfIndex] = useState({});       // performanceIndex (0–5)
  const [participants, setParticipants] = useState([]); // UIDs who actually participated
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [attendanceAdded, setAttendanceAdded] = useState(false);
  const [resultAnnounced, setResultAnnounced] = useState(false);

  // Load base data when dialog opens
  useEffect(() => {
    if (!open || !event?.id) return;

    let cancelled = false;

    (async () => {
      try {
        const [empSnap, attSnap, perfSnap, metaSnap] = await Promise.all([
          get(ref(db, "/employees")),
          get(ref(db, `/events/${event.id}/attendance`)),
          get(ref(db, `/events/${event.id}/performanceIndex`)),
          get(ref(db, `/events/${event.id}/metadata`)),
        ]);
        if (cancelled) return;

        setEmployees(empSnap.val() || {});
        setPoints(attSnap.val() || {});
        setPerfIndex(perfSnap.val() || {});
        const meta = metaSnap.val() || {};
        setAttendanceAdded(!!meta.attendanceAdded);
        setResultAnnounced(!!meta.resultAnnounced);
      } catch (e) {
        console.error("Attendance dialog init failed:", e);
      }
    })();

    // participants: those who actually submitted under performance
    const partRef = ref(db, `/eventStatusByUser/${event.id}/performance`);
    const partCb = (snap) => {
      const raw = snap.val() || {};
      const keys = Object.keys(raw || {});
      setParticipants(Array.isArray(keys) ? keys : []);
    };
    onValue(partRef, partCb);

    return () => {
      cancelled = true;
      off(partRef, "value", partCb);
    };
  }, [open, event?.id]);

  // ONLY participants, with robust names + numbering
  const eligible = useMemo(() => {
    if (!Array.isArray(participants)) return [];
    const rows = participants.map((uid) => {
      const e = employees?.[uid] || {};
      const name =
        e?.submittedProfile?.profileFields?.fullName ||
        e?.fullName ||
        String(uid); // fallback to uid, never "Unnamed"
      return { uid, name };
    });
    rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows.map((r, i) => ({ ...r, index: i + 1 }));
  }, [participants, employees]);

  const handleChangeAttendance = (uid, val) => {
    const num = Math.max(0, Math.min(4, Number(val)));
    setPoints((prev) => ({ ...prev, [uid]: Number.isFinite(num) ? num : 0 }));
  };

  const handleChangePerfIndex = (uid, val) => {
    const num = Math.max(0, Math.min(5, Number(val)));
    setPerfIndex((prev) => ({ ...prev, [uid]: Number.isFinite(num) ? num : 0 }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const updates = {};
      const eid = event.id;

      // Keep your write schema IDENTICAL (attendance)
      eligible.forEach(({ uid }) => {
        const attVal = points[uid] ?? 0;
        const perfVal = perfIndex[uid] ?? 0;

        // attendance paths (unchanged)
        updates[`/events/${eid}/attendance/${uid}`] = attVal;
        updates[`/events/${eid}/results/${uid}/attendance`] = attVal;

        // NEW: performanceIndex path (requested)
        updates[`/events/${eid}/performanceIndex/${uid}`] = perfVal;
      });

      updates[`/events/${eid}/metadata/attendanceAdded`] = true;

      await update(ref(db), updates);
      setSaved(true);
      setAttendanceAdded(true);
      setTimeout(() => setSaved(false), 1200);
    } catch (err) {
      console.error("Error saving attendance/performanceIndex:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        Attendance & Performance
        {attendanceAdded && (
          <Chip label="Attendance added" color="success" size="small" variant="outlined" />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Values saved successfully!
          </Alert>
        )}

        {resultAnnounced && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Results have been announced — editing is disabled.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set per-participant scores. Final total is <b>15</b>:
          {" "}
          <b>Performance (0–5)</b> + <b>Peer Ratings (0–5)</b> + <b>Micro (0–1)</b> + <b>Attendance (0–4)</b>.
          <br />
          Eligibility: <b>Performance ≥ 3/5</b> and <b>(Attendance + Micro) ≥ 3</b>.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {eligible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No participants found yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {eligible.map(({ uid, name, index }) => (
              <Grid item xs={12} key={uid}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
                    <Chip size="small" label={`#${index}`} />
                    <Typography sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {name}
                    </Typography>
                  </Box>

                  {/* Attendance (0–4) */}
                  <Tooltip title="0–4 points" arrow>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, max: 4, step: 0.5, readOnly: resultAnnounced }}
                      value={points[uid] === 0 ? 0 : (points[uid] ?? "")}
                      onChange={(e) => handleChangeAttendance(uid, e.target.value)}
                      label="Attendance (0–4)"
                      sx={{ width: 150 }}
                    />
                  </Tooltip>

                  {/* Performance Index (0–5) */}
                  <Tooltip title="0–5 points" arrow>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, max: 5, step: 0.5, readOnly: resultAnnounced }}
                      value={perfIndex[uid] === 0 ? 0 : (perfIndex[uid] ?? "")}
                      onChange={(e) => handleChangePerfIndex(uid, e.target.value)}
                      label="Performance (0–5)"
                      sx={{ width: 170 }}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {!resultAnnounced && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || eligible.length === 0}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {attendanceAdded ? "Update" : "Save"}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
